import React from 'react'
import SvgIcon from 'components/atoms/SvgIcon'
import {
  AvatarImage,
  Button,
  InputRadio,
  InputText,
  InputTextArea,
  Select,
  CheckBox,
} from 'components/atoms'
import { Form } from 'components/molecules'
import styled from 'styled-components'
import {
  IBuildedTour,
  IConflictTour,
  IWeeklyGuide,
  IWeeklyTourGroup,
  IWeeklySoundOutTemplate,
  IWeeklyFormalRequestTemplate,
} from 'utils/weeklyTypes'
import { COLORS, REQUESTING_STATUSES } from 'static/constants'
import * as interfaces from 'static/interfaces'
import { agentService } from 'core/services'
import I18n from 'core/i18n'
import { getTourGroupLabel } from 'utils/weekly'
import {
  getTemplateById,
  getTemplateOptions,
  getDefaultTemplateId,
  getFormalRequestWarnings,
  replaceVariableForMail,
  getInitialRequestAgentRankId,
} from 'utils/soundoutOrFormalRequest'
import { ConflictTourLink } from './ConflictTourLink'
import { GuideKindSelector } from 'components/molecules/GuideKindSelector'

declare var window: interfaces.IWindow

interface IErrors {
  [key: string]: string | null
}

interface IProps {
  agent: any
  guide: IWeeklyGuide
  tour: IBuildedTour
  latestConflictingTour: IConflictTour | null
  messageType: 'sound_out' | 'formal'
  resend?: boolean
  onTourUpdate: (tour: any) => void
}

interface ITemplate {
  id: string
  title: string
  subject?: string
  message?: string
}

const FIELDS = {
  mail_subject: 'mail_subject',
  message: 'message',
  send_mail: 'send_mail',
}

const INIT_MESSAGE = `[start_at]催行の[tour_name]ガイド打診が届いています。
受付可否の回答をお願い致します。
`

const InputSection = ({ label, children, className = '' }) => {
  return (
    <div className={`input-section ${className}`}>
      <div className="label">{label}</div>
      <div className="input-container">{children}</div>
    </div>
  )
}

export const SoundoutOrFormalRequestModalBody: React.FC<IProps> = ({
  agent,
  guide,
  tour,
  latestConflictingTour,
  messageType,
  resend = false,
  onTourUpdate,
}) => {
  const [selectedMessageType, setSelectedMessageType] = React.useState<'sound_out' | 'formal'>(
    messageType
  )
  const [selectedTemplate, setSelectedTemplate] = React.useState<ITemplate | null>(null)
  const [sending, setSending] = React.useState(false)
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(!!selectedTemplate)
  const [errors, setErrors] = React.useState<IErrors>({})

  // 打診時にツアーグループを指定済みなら、それを初期選択にする(正式依頼の送信時など)
  // 採番対象のツアーで、打診・正式依頼中なのに指定がない(指定グループが削除された)ガイドは「未指定」から始める。
  // 先頭グループに黙って切り替わり、意図しないグループが依頼先として保存されるのを防ぐ
  const currentTourGuide = tour.tour_guides.find(tg => tg.guide_id === guide.id)
  const isRequestingWithoutGroup =
    tour.last_tour_group_number != null &&
    REQUESTING_STATUSES.includes(currentTourGuide?.request_status) &&
    !currentTourGuide?.requested_tour_group_id
  const initTourGroup = isRequestingWithoutGroup
    ? undefined
    : tour.tour_groups.find(g => g.id === currentTourGuide?.requested_tour_group_id) ||
      tour.tour_groups[0]
  const [selectedTourGroup, setSelectedTourGroup] = React.useState<IWeeklyTourGroup>(initTourGroup)

  // 打診中・承認済みは依頼時の値、一斉募集の応募・見送りからの正式依頼は応募先の募集の現在の値を使う
  const [selectedAgentRankId, setSelectedAgentRankId] = React.useState<number | null>(
    getInitialRequestAgentRankId(currentTourGuide, messageType)
  )

  // 集合場所・テンプレ・特殊ランク・料金はモーダル表示時にAPIで取得する
  const [loading, setLoading] = React.useState(true)
  const [meetingPointName, setMeetingPointName] = React.useState<string | null>(null)
  const [soundoutTemplates, setSoundoutTemplates] = React.useState<IWeeklySoundOutTemplate[]>([])
  const [formalRequestTemplates, setFormalRequestTemplates] = React.useState<
    IWeeklyFormalRequestTemplate[]
  >([])
  // デフォルトテンプレ判定用の対象post id(優先順)。サーバが返す
  const [targetPostIds, setTargetPostIds] = React.useState<number[]>([])
  const [agentRanks, setAgentRanks] = React.useState<interfaces.IAgentRank[]>([])
  const [postRankFees, setPostRankFees] = React.useState<
    Array<{ agent_rank_id: number; amount: number }>
  >([])
  // ツアーの支払設定の未設定項目('guide_fee' | 'post_cost')。正式依頼時に警告表示する
  const [missingPaymentSettings, setMissingPaymentSettings] = React.useState<string[]>([])

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const {
          meeting_point_name,
          target_post_ids,
          sound_out_templates,
          formal_request_templates,
          agent_ranks,
          post_rank_fees,
          missing_payment_settings,
        } = await agentService.getPostRequestTemplates(Number(tour.post_id))
        setMeetingPointName(meeting_point_name)
        setTargetPostIds(target_post_ids)
        setSoundoutTemplates(sound_out_templates)
        setFormalRequestTemplates(formal_request_templates)
        setAgentRanks(agent_ranks)
        setPostRankFees(post_rank_fees)
        setMissingPaymentSettings(missing_payment_settings)
      } catch (e) {
        console.error(e)
        window.flashMessages.addMessage({
          text: 'テンプレートの取得に失敗しました。',
          type: 'error',
        })
      } finally {
        setLoading(false)
      }
    }
    fetchTemplates()
  }, [tour.post_id])

  // 特殊ランクの選択肢は、このツアーに料金(post_rank_fee)が設定されているランクのみ
  const specialRankOptions = React.useMemo(
    () =>
      agentRanks
        .filter(rank =>
          postRankFees.some(fee => fee.agent_rank_id === rank.id && fee.amount != null)
        )
        .map(rank => ({ value: String(rank.id), label: rank.name })),
    [agentRanks, postRankFees]
  )

  // [guide_fee]用: 特殊は選択ランク、通常は送信先ガイドのランクの料金
  const feeRankId = selectedAgentRankId ?? guide.agent_rank_as_guide?.id ?? null
  const guideFeeAmount =
    feeRankId != null
      ? postRankFees.find(fee => fee.agent_rank_id === feeRankId)?.amount ?? null
      : null

  // 再描画防止のため、テンプレートの選択肢はuseMemoでキャッシュする
  const templateOptions = React.useMemo(() => {
    const currentTemplate =
      selectedMessageType === 'sound_out' ? soundoutTemplates : formalRequestTemplates
    return getTemplateOptions(currentTemplate, selectedAgentRankId)
  }, [selectedMessageType, soundoutTemplates, formalRequestTemplates, selectedAgentRankId])

  const tourSettingLink = `/agent/posts/${tour.post.id}/edit?active=settings`

  const handleSubmit = async (_, values) => {
    setSending(true)
    const hasTourId = !!tour.id
    let tourId = tour.id
    try {
      if (selectedMessageType === 'sound_out') {
        const { tourId: updatedTourId } = await handleSubmitSoundout({
          message: values.message,
          mail_subject: values.mail_subject,
          send_mail: values.send_mail,
        })
        tourId = updatedTourId
      } else {
        const { tourId: updatedTourId } = await handleSubmitFormalRequest({
          mail_subject: values.mail_subject,
          mail_body: values.message,
          send_mail: values.send_mail,
        })
        tourId = updatedTourId
      }
      window.flashMessages.addMessage({ text: I18n.t('generic.send_success') })
      const { tour: updatedTour } = await agentService.getWeeklyTour(tourId)
      onTourUpdate(updatedTour)
      setSending(false)
      if (!hasTourId) {
        const url = new URL(window.location.href)
        url.searchParams.set('tour_id', String(tourId))
        window.history.replaceState(null, '', `${url.pathname}?${url.searchParams.toString()}`)
      }
      window.globalModal.closeModal()
    } catch (e) {
      console.error(e)
      setSending(false)
    }
  }

  const handleSubmitSoundout = async ({ message, mail_subject, send_mail }) => {
    const sound_out = {
      tour_attributes: {
        id: tour.id,
        post_id: tour.post_id,
        day: tour.day,
        start_time: tour.start_time,
      },
      tour_guide_attributes: {
        guide_id: guide.id.toString(),
        request_status: 'agent_pre_requested',
        agent_rank_id: selectedAgentRankId,
        requested_tour_group_id: selectedTourGroup?.id ?? null,
      },
    }
    return await agentService.postSoundOuts({
      sound_out,
      mail_body: message,
      mail_subject,
      send_mail,
    })
  }

  const handleSubmitFormalRequest = async ({ mail_subject, mail_body, send_mail }) => {
    const formal_request = {
      tour_attributes: {
        id: tour.id,
        post_id: tour.post_id,
        day: tour.day,
        start_time: tour.start_time,
      },
      tour_guide_attributes: {
        guide_id: guide.id.toString(),
        request_status: 'agent_official_requested',
        agent_rank_id: selectedAgentRankId,
        requested_tour_group_id: selectedTourGroup?.id ?? null,
      },
    }
    return await agentService.postFormalRequests({
      formal_request,
      mail_subject,
      mail_body,
      send_mail,
    })
  }

  React.useEffect(() => {
    if (templateOptions.length > 0) {
      // 選択中グループのデフォルトテンプレを優先し、なければ先頭を初期選択にする
      const currentTemplates =
        selectedMessageType === 'sound_out' ? soundoutTemplates : formalRequestTemplates
      const defaultTemplateId = getDefaultTemplateId(
        currentTemplates,
        targetPostIds,
        selectedAgentRankId
      )
      const initialTemplate = getTemplateById(
        defaultTemplateId ?? templateOptions[0].id,
        selectedMessageType,
        soundoutTemplates,
        formalRequestTemplates
      )
      setSelectedTemplate(initialTemplate)
      handleUpdateForm({}, !!initialTemplate)
    } else {
      setSelectedTemplate(null)
      handleUpdateForm({}, false)
    }
  }, [templateOptions])

  const handleUpdateForm = (updatedErrors, updatedIsSubmitEnabled) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }

  const resendLabel =
    selectedMessageType === 'sound_out' ? '再度打診を行いますか？' : '再度正式依頼を行いますか？'
  const formalRequestWarnings = getFormalRequestWarnings({
    messageType: selectedMessageType,
    missingPaymentSettings,
    selectedAgentRankId,
    guideHasRank: !!guide.agent_rank_as_guide,
    postId: tour.post.id,
    guideId: guide.id,
  })

  return (
    <ModalBody>
      <Form fields={FIELDS} handleSubmit={handleSubmit} handleUpdateForm={handleUpdateForm}>
        <div className="header">
          <div>{resend ? resendLabel : 'メッセージ送信'}</div>
          <SvgIcon
            className="close-button"
            name="close"
            width={24}
            height={24}
            role="button"
            onClick={() => {
              window.globalModal.closeModal()
            }}
          />
        </div>
        <div className="contents">
          <InputSection label="ガイド">
            <div className="guide-info">
              <AvatarImage size={32} avatarURL={guide.avatar_url} />
              <div>{guide.username}</div>
            </div>
          </InputSection>
          {latestConflictingTour && (
            <InputSection label="">
              <ConflictTourLink conflictTour={latestConflictingTour} />
            </InputSection>
          )}
          <InputSection label="ガイドの種類">
            <GuideKindSelector
              name="guideKind"
              specialRankOptions={specialRankOptions}
              agentRankId={selectedAgentRankId}
              onChange={setSelectedAgentRankId}
            />
          </InputSection>

          <InputSection label="ツアーグループ">
            <Select
              name="tourGroup"
              disabled={tour.tour_groups.length === 0}
              options={
                tour.tour_groups.length === 0
                  ? [{ id: '', label: 'ツアーグループ未作成', value: '' }]
                  : [
                      { id: '', label: '未指定', value: '' },
                      ...tour.tour_groups.map(g => ({
                        id: g.id.toString(),
                        label: getTourGroupLabel(g),
                        value: g.id.toString(),
                      })),
                    ]
              }
              value={selectedTourGroup?.id ? String(selectedTourGroup.id) : ''}
              onChangeHandler={e => {
                const group = tour.tour_groups.find(g => String(g.id) === e.target.value)
                setSelectedTourGroup(group)
              }}
            />
          </InputSection>
          <InputSection label="メッセージタイプ">
            {/* input-containerがflex(row)のため、ラジオと警告を縦に並べるためのラッパー */}
            <div>
              <InputRadio
                directionRow={true}
                name="messageType"
                options={[
                  { id: 'sound_out', label: '打診メッセージ', value: 'sound_out' },
                  { id: 'formal', label: '正式依頼', value: 'formal' },
                ]}
                defaultValue={messageType}
                onChangeHandler={e => {
                  setSelectedMessageType(e.target.value)
                }}
              />
              {formalRequestWarnings.length > 0 && (
                <div className="payment-settings-warning">
                  <ul>
                    {formalRequestWarnings.map(warning => (
                      <li key={warning.message}>
                        {warning.message}
                        <a href={warning.href} target="_blank" rel="noopener noreferrer">
                          {warning.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </InputSection>
          <InputSection label="メッセージテンプレート">
            <>
              <div className="select">
                <Select
                  name="soundOutTemplate"
                  options={templateOptions}
                  value={String(selectedTemplate?.id)}
                  onChangeHandler={e => {
                    const template = getTemplateById(
                      e.target.value,
                      selectedMessageType,
                      soundoutTemplates,
                      formalRequestTemplates
                    )
                    setSelectedTemplate(template)
                  }}
                />
              </div>
              <a
                href={tourSettingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="tour-setting-link"
              >
                ツアー設定
              </a>
            </>
          </InputSection>
          <InputSection label="件名">
            <InputText
              name="mail_subject"
              placeholder="件名を入力してください"
              value={replaceVariableForMail(
                selectedTemplate?.subject || '',
                tour,
                guide,
                agent,
                selectedTourGroup,
                guideFeeAmount,
                meetingPointName
              )}
              required={true}
            />
          </InputSection>
          <InputSection label="本文" className="top">
            <InputTextArea
              name="message"
              placeholder="本文が未入力です"
              defaultValue=""
              className="message-textarea"
              value={replaceVariableForMail(
                selectedTemplate?.message || INIT_MESSAGE,
                tour,
                guide,
                agent,
                selectedTourGroup,
                guideFeeAmount,
                meetingPointName
              )}
              fullWidth={true}
              required={true}
            />
          </InputSection>
          <div className="send-notification">
            <CheckBox
              name="send_mail"
              label="メールを送信する"
              showLabelInline={true}
              defaultChecked={false}
            />
          </div>
          <div className="footer">
            <Button
              disabled={sending}
              borderColor={COLORS.primary}
              handleClick={() => {
                window.globalModal.closeModal()
              }}
            >
              キャンセル
            </Button>

            <Button primary={true} disabled={!isSubmitEnabled || sending || loading}>
              依頼実行
            </Button>
          </div>
        </div>
      </Form>
    </ModalBody>
  )
}

const ModalBody = styled.div`
  .payment-settings-warning {
    margin-top: 8px;
    padding: 8px 12px;
    border: 1px solid ${COLORS.useally};
    border-radius: 4px;
    background-color: ${COLORS.useallyBG};
    color: ${COLORS.useallyText};
    font-size: 12px;

    ul {
      padding-left: 1.2em;
      list-style: disc;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    a {
      color: ${COLORS.primary};
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    }
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    font-size: 18px;
    border-bottom: 1px solid ${COLORS.divider};
    padding: 18px 24px;

    .close-button {
      cursor: pointer;
      width: 24px;
      height: 24px;
    }
  }

  .contents {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;

    .input-section {
      display: flex;
      align-items: center;
      gap: 24px;
      &.top {
        align-items: flex-start;
      }
      .label {
        font-weight: 600;
        text-align: right;
        min-width: 222px;
        flex: 0;
      }
      .guide-info {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .input-container {
        .select {
          max-width: 200px;
        }
        > div {
          width: 100%;
        }

        > a {
          color: ${COLORS.primary};
          text-decoration: underline;
          &:hover {
            text-decoration: none;
          }
        }

        font-size: 14px;
        display: flex;
        align-items: center;
        width: 100%;
        gap: 16px;

        .message-textarea {
          height: 320px;
        }
      }
    }

    .send-notification {
      align-self: flex-end;
    }

    .footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
`
